import LockIcon from "@mui/icons-material/Lock";
import { Box, Button, Chip, Typography, Zoom } from "@mui/material";
import dayjs from "dayjs";
import { Category, CartItem, EventDetails } from "../types";
import { PremiumButton, QuantityButton, StyledTicketCard } from "../styles";
import { rupiah } from "../../../../utils/currency";

interface TicketCardProps {
  category: Category;
  cartItem: CartItem | undefined;
  eventDetails: EventDetails;
  index: number;
  onAdd: (category: Category) => void;
  onDecrease: (category: Category) => void;
}

export function TicketCard({ 
  category, 
  cartItem, 
  eventDetails, 
  index, 
  onAdd, 
  onDecrease 
}: TicketCardProps) {
  const saleStarted = category.is_started;
  const saleEnded = category.is_ended;

  const getAvailabilityMessage = (category: Category) => {
    if (category.is_full_booked) {
      return "FULLY BOOKED";
    }
    if (category.show_last_tickets) {
      return `LAST ${category.remaining_available} TICKETS`;
    }
    if (!category.is_full_booked && !category.is_available) {
      return `SOLD OUT`
    }
    return "AVAILABLE";
  };

  const getAvailabilityColor = (category: Category) => {
    if (category.is_full_booked || (!category.is_full_booked && !category.is_available)) {
      return "error";
    }
    if (category.show_last_tickets) {
      return "warning";
    }
    return "success";
  };

  if (!category.is_active) {
    return null;
  }

  return (
    <Zoom in timeout={600 + index * 100}>
      <StyledTicketCard>
        {/* Lock Overlay */}
        {(saleStarted === false || saleEnded === true) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              borderRadius: { xs: '12px', md: '16px' },
              zIndex: 10,
            }}
          >
            {!(!category.is_full_booked && !category.is_available) && !saleStarted && (
              <LockIcon sx={{ fontSize: 40, color: 'white', mb: 1 }} />
            )}
            <Typography variant="h6" fontWeight="bold" color="white" textAlign="center">
              {!category.is_full_booked && !category.is_available ? "Sold Out" :
                saleStarted
                  ? "Not Available"
                  : `Sale starts ${dayjs(category.start_time).format("MMM DD, HH:mm")}`}
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                {category.name}
              </Typography>
              <Chip
                size="small"
                label={getAvailabilityMessage(category)}
                color={getAvailabilityColor(category)}
                sx={{ 
                  fontWeight: 600, 
                  fontSize: '0.7rem',
                  height: '24px'
                }}
              />
            </Box>
            
            <Typography 
              variant="body2" 
              color="textSecondary" 
              sx={{ mb: 2, lineHeight: 1.5 }}
            >
              {category.description}
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold" color="primary">
                {rupiah(category.price)}
              </Typography>

              {category.is_available ? (
                cartItem ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <QuantityButton
                      size="small"
                      onClick={() => onDecrease(category)}
                    >
                      -
                    </QuantityButton>
                    <Typography 
                      variant="body1" 
                      fontWeight="bold"
                      sx={{ 
                        minWidth: '30px', 
                        textAlign: 'center',
                        px: 1
                      }}
                    >
                      {cartItem.quantity}
                    </Typography>
                    <QuantityButton
                      size="small"
                      onClick={() => onAdd(category)}
                      disabled={cartItem.quantity >= eventDetails.max_purchase}
                    >
                      +
                    </QuantityButton>
                  </Box>
                ) : (
                  <PremiumButton
                    variant="contained"
                    size="small"
                    onClick={() => onAdd(category)}
                    disabled={saleStarted === false || saleEnded === true}
                  >
                    Add
                  </PremiumButton>
                )
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  disabled
                  sx={{ 
                    borderColor: 'error.main',
                    color: 'error.main',
                    fontSize: '0.75rem'
                  }}
                >
                  {category.is_full_booked ? "Booked" : "Sold Out"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </StyledTicketCard>
    </Zoom>
  );
}
